import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DashboardQueryParams } from '@/types/dashboard';
import type { ReportGroupBy } from '@/types/report';

export interface DashboardPageState {
  dateFrom: string;
  dateTo: string;
  groupBy: ReportGroupBy;
}

const DEFAULT_GROUP_BY: ReportGroupBy = 'day';

function defaultDateRange() {
  return {
    dateFrom: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    dateTo: dayjs().format('YYYY-MM-DD'),
  };
}

function parseGroupBy(value: string | null): ReportGroupBy {
  if (value === 'week' || value === 'month') return value;
  return DEFAULT_GROUP_BY;
}

export function useDashboardQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSyncedDefaults = useRef(false);

  const state = useMemo<DashboardPageState>(() => {
    const defaults = defaultDateRange();
    return {
      dateFrom: searchParams.get('dateFrom') ?? defaults.dateFrom,
      dateTo: searchParams.get('dateTo') ?? defaults.dateTo,
      groupBy: parseGroupBy(searchParams.get('groupBy')),
    };
  }, [searchParams]);

  useEffect(() => {
    if (hasSyncedDefaults.current) return;

    const defaults = defaultDateRange();
    const needsDateFrom = !searchParams.get('dateFrom');
    const needsDateTo = !searchParams.get('dateTo');
    const needsGroupBy = !searchParams.get('groupBy');

    if (!needsDateFrom && !needsDateTo && !needsGroupBy) {
      hasSyncedDefaults.current = true;
      return;
    }

    hasSyncedDefaults.current = true;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (needsDateFrom) next.set('dateFrom', defaults.dateFrom);
        if (needsDateTo) next.set('dateTo', defaults.dateTo);
        if (needsGroupBy) next.set('groupBy', DEFAULT_GROUP_BY);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const apiParams = useMemo<DashboardQueryParams>(
    () => ({
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      groupBy: state.groupBy,
      limit: 8,
    }),
    [state.dateFrom, state.dateTo, state.groupBy],
  );

  const dateRangeValue = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(state.dateFrom), dayjs(state.dateTo)],
    [state.dateFrom, state.dateTo],
  );

  const patchParams = useCallback(
    (patch: Partial<DashboardPageState>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (patch.dateFrom !== undefined) next.set('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) next.set('dateTo', patch.dateTo);
          if (patch.groupBy !== undefined) next.set('groupBy', patch.groupBy);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    const range = defaultDateRange();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('dateFrom', range.dateFrom);
        next.set('dateTo', range.dateTo);
        next.set('groupBy', DEFAULT_GROUP_BY);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return {
    state,
    apiParams,
    dateRangeValue,
    patchParams,
    resetFilters,
  };
}
